import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ReactiveFormsModule, FormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ButtonComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { UserService } from '../../../../services/user.service';

@Component({
  selector: 'ppl-purchase-adjust-void-entry',
  standalone: true,
  imports: [
    CommonModule,
    NgZorroModule,
    ButtonComponent,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './purchase-adjust-void-entry.component.html',
  styleUrl: './purchase-adjust-void-entry.component.css'
})
export class PurchaseAdjustVoidEntryComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() purchaseData?: any;
  @Input() searchParams: any;
  @Input() actionData: any;
  @Output() purchaseAdjustedOrVoided = new EventEmitter();

  adjustVoidPurchaseForm!: FormGroup;
  reasonList: LookupValueItem[] = [];
  serviceCodesData: any[] = [];
  serviceDescriptions: any[] = [];
  // Read-only display fields driven by the selected Service Description (authList entry).
  selectedPplAuthNum = '';
  selectedRegionalCenter = '';
  isAuthFail = false;
  buttonLabel = '';
  actionType = '';
  programName!: string;
  hasPurchaseAdjustError = false;
  purchaseAdjustErrorMessage = '';

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';

    this.actionType = this.actionData.buttonAction;
    this.buttonLabel = this.actionData.button_label;

    this.reasonList = this.lookupService.getLookupValue('adjustment_reason');

    const isAdjust = this.actionType === 'adjustPurchase';
    const isVoidSingle = this.actionType === 'voidPurchase';

    this.adjustVoidPurchaseForm = this.fb.group({
      serviceCode: [null],
      serviceDescription: [null],
      unit: [isAdjust ? (this.purchaseData?.unit ?? null) : null],
      price: [isAdjust ? (this.purchaseData?.price ?? null) : null],
      crmNumber: [this.purchaseData?.crmNumber ?? null],
      taxAmount: [isAdjust || isVoidSingle ? (this.purchaseData?.taxAmount ?? null) : null],
      shippingAmount: [isAdjust || isVoidSingle ? (this.purchaseData?.shippingAmount ?? null) : null],
      reason: [null, [Validators.required]],
      reasonDetails: [null, [Validators.maxLength(160)]],
    });

    if (isAdjust) {
      ['serviceCode', 'serviceDescription', 'unit', 'price', 'crmNumber', 'taxAmount', 'shippingAmount']
        .forEach(field => {
          this.adjustVoidPurchaseForm.get(field)?.setValidators([Validators.required]);
          this.adjustVoidPurchaseForm.get(field)?.updateValueAndValidity();
        });
      this.loadServiceCodesByConsumer();
    } else {
      this.adjustVoidPurchaseForm.get('crmNumber')?.setValidators([Validators.required]);
      this.adjustVoidPurchaseForm.get('crmNumber')?.updateValueAndValidity();
    }

    this.adjustVoidPurchaseForm.get('reason')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((reason: any) => {
        const reasonDetailsControl = this.adjustVoidPurchaseForm.get('reasonDetails');
        const isOtherReason = !!reason?.lkpValueName?.toLowerCase()?.startsWith('other');
        if (isOtherReason) {
          reasonDetailsControl?.setValidators([Validators.required, Validators.maxLength(160)]);
        } else {
          reasonDetailsControl?.setValidators([Validators.maxLength(160)]);
        }
        reasonDetailsControl?.updateValueAndValidity();
      });
  }

  private loadServiceCodesByConsumer(): void {
    const submissionDate = this.purchaseData?.submissionDate;

    const payload = {
      programCode: this.programName,
      consumerId: this.purchaseData?.participantId,
      periodFromDate: submissionDate,
      periodToDate: submissionDate,
      source: 'purchaserequest',
      providerId: null
    };

    this.timesheetService
      .getServiceCodesByConsumer(payload)
      .subscribe((data: any) => {
        if ((data.responsedata === 'Auth Fail' && data.status === 404) ||
          !data.responsedata || (Array.isArray(data.responsedata) && data.responsedata.length === 0)) {
          this.isAuthFail = true;
          return;
        }

        this.serviceCodesData = data.responsedata;

        const matchedServiceCode = this.serviceCodesData.find((item: any) =>
          item.serviceCodeDetailId === this.purchaseData?.serviceCodeDetailId
        );
        if (matchedServiceCode) {
          matchedServiceCode.authList = matchedServiceCode.authList ?? [];

          const existingAuthId = this.purchaseData?.tasAuthorizationDetailId;
          const existingAuthDesc = this.purchaseData?.authDesc;
          const hasMatch = matchedServiceCode.authList.some((d: any) =>
            d.ids === existingAuthId && d.desc === existingAuthDesc
          );
          if (!hasMatch && existingAuthId !== undefined && existingAuthDesc) {
            matchedServiceCode.authList.push({ ids: existingAuthId, desc: existingAuthDesc });
          }

          this.serviceDescriptions = this.sortAuthListByDesc(matchedServiceCode.authList);
          this.adjustVoidPurchaseForm.patchValue({ serviceCode: matchedServiceCode.serviceCodeDetailId });

          const matchedDescription = this.serviceDescriptions.find((d: any) =>
            d.ids === existingAuthId && d.desc === existingAuthDesc
          );
          if (matchedDescription) {
            this.adjustVoidPurchaseForm.patchValue({ serviceDescription: matchedDescription.ids });
            this.setAuthDisplayFields(matchedDescription);
          }
        }
      });
  }

  onServiceCodeChange(serviceCodeDetailId: any): void {
    const matched = this.serviceCodesData.find((item: any) =>
      item.serviceCodeDetailId === serviceCodeDetailId
    );
    this.serviceDescriptions = this.sortAuthListByDesc(matched?.authList ?? []);
    this.adjustVoidPurchaseForm.patchValue({ serviceDescription: null });
    this.setAuthDisplayFields(null);
  }

  /** Updates the read-only PPL Authorization Number / Regional Center display for the selected Service Description. */
  onServiceDescriptionChange(ids: any): void {
    const matched = this.serviceDescriptions.find((d: any) => d.ids === ids);
    this.setAuthDisplayFields(matched);
  }

  private setAuthDisplayFields(authListItem: any): void {
    this.selectedPplAuthNum = authListItem?.pplAuthNum ?? '';
    this.selectedRegionalCenter = authListItem?.orgName ?? '';
  }

  private sortAuthListByDesc(authList: any[]): any[] {
    return [...authList].sort((a, b) =>
      (a?.desc ?? '').localeCompare(b?.desc ?? '')
    );
  }

  // ─── Save ───────────────────────────────────────────────────────────────────

  onSave(): void {
    if (this.adjustVoidPurchaseForm?.valid) {
      this.hasPurchaseAdjustError = false;
      this.purchaseAdjustErrorMessage = '';

      switch (this.actionType) {

        case 'adjustPurchase': {
          const f = this.adjustVoidPurchaseForm;
          const reasonName = f.get('reason')?.value?.lkpValueName;
          const reasonDetails = f.get('reasonDetails')?.value?.toString().trim();
          const payload = {
            purchaseRequestLineId: this.purchaseData?.purchaseRequestLineId,
            serviceCode: Number(f.get('serviceCode')?.value),
            authDesc: f.get('serviceDescription')?.value,
            reason: reasonDetails ? `${reasonName} - ${reasonDetails}` : reasonName,
            unit: Number(f.get('unit')?.value),
            price: Number(f.get('price')?.value),
            crmNumber: f.get('crmNumber')?.value?.toString().trim(),
            taxAmount: Number(f.get('taxAmount')?.value),
            shippingAmount: Number(f.get('shippingAmount')?.value)
          };
          this.timesheetService
            .adjustPurchase(payload, this.programName)
            .subscribe({
              next: (res: any) => this.handlePurchaseResponse(res),
              error: (err: any) => this.showPurchaseAdjustError(err?.error)
            });
          break;
        }

        case 'voidPurchase': {
          const taxAmountValue = this.adjustVoidPurchaseForm.get('taxAmount')?.value;
          const shippingAmountValue = this.adjustVoidPurchaseForm.get('shippingAmount')?.value;
          const payload = {
            purchaseRequestLineId: this.purchaseData?.purchaseRequestLineId,
            reason: this.adjustVoidPurchaseForm.get('reason')?.value?.lkpValueName,
            crmNumber: this.adjustVoidPurchaseForm.get('crmNumber')?.value?.toString().trim(),
            taxAmount: taxAmountValue !== null && taxAmountValue !== '' ? Number(taxAmountValue) : null,
            shippingAmount: shippingAmountValue !== null && shippingAmountValue !== '' ? Number(shippingAmountValue) : null
          };
          this.timesheetService
            .adjustVoidPurchase(payload, this.programName)
            .subscribe({
              next: (res: any) => this.handlePurchaseResponse(res),
              error: (err: any) => this.showPurchaseAdjustError(err?.error)
            });
          break;
        }
        // Void All action — temporarily hidden, may be reintroduced later
        // case 'voidAllPurchases': {
        //   const payload = {
        //     purchaseRequestLineIds: this.purchaseData?.purchaseRequestLineId,
        //     reason: this.adjustVoidPurchaseForm.get('reason')?.value?.lkpValueName,
        //     crmNumber: this.adjustVoidPurchaseForm.get('crmNumber')?.value?.toString().trim()
        //   };
        //   this.timesheetService
        //     .adjustVoidAllPurchase(payload, this.programName)
        //     .subscribe({
        //       next: (res: any) => this.handlePurchaseResponse(res),
        //       error: (err: any) => this.showPurchaseAdjustError(err?.error)
        //     });
        //   break;
        // }
      }
    } else {
      this.adjustVoidPurchaseForm.markAllAsTouched();
    }
  }

  private handlePurchaseResponse(res: any): void {
    if (res?.responsedata === 'failure') {
      this.showPurchaseAdjustError(res);
      return;
    }
    this.purchaseAdjustedOrVoided.emit({});
  }

  private showPurchaseAdjustError(response?: any): void {
    this.hasPurchaseAdjustError = true;
    this.purchaseAdjustErrorMessage = response?.responsemsg?.[0]?.desc
      ?? 'Something went wrong while processing your request. Please try again.';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
